import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useGetAsset,
  useGetClock,
  useGetOptionExpirations,
} from "~/v2/api/hooks";
import { useGetOptionsData } from "../../hooks/useGetOptionsData";
import { useUnderlyingSpot } from "../../hooks/useUnderlyingSpot";
import {
  type LegQuoteSide,
  MAX_TICKET_LEGS,
  type OrderLeg,
} from "../../shared/optionLegs/optionLeg.types";
import { createDefaultPlacement } from "../../shared/workspace/placementAdapters";
import { selectOptionTargetTicketId } from "../../shared/workspace/ticketRouting.slice";
import type { WorkspaceStoreApi } from "../../shared/workspace/Workspace.context";
import type {
  GroupId,
  WorkspaceStore,
} from "../../shared/workspace/workspace.types";
import { normalizeWorkspaceSymbol } from "../../shared/workspace/workspace.utils";
import type {
  ChainMetricId,
  MetricDropDirection,
} from "./optionsChain.columns";
import { resolveVisibleMetrics } from "./optionsChain.columns";
import type {
  ChainEmptyReason,
  ChainLeg,
  ChainRow,
  ChainSection,
  ChainSideFilter,
  ExpirationCount,
  ExpirationFilter,
  ExpirationRange,
  StrikeCount,
  ValueFreshness,
} from "./optionsChain.types";
import {
  buildChainRows,
  buildExpirationOptions,
  classifyFreshness,
  filterExpirations,
  formatExpirationLabel,
  reuseUnchangedRows,
  windowStrikes,
} from "./optionsChain.utils";
import type { OptionsChainPreferences } from "./useOptionsChainPreferences";

/** Stable fallback so the selection selector never returns a fresh array. */
const EMPTY_ROWS: ChainRow[] = [];
const EMPTY_SECTIONS: ChainSection[] = [];

const NO_SELECTION: { symbol: string; side: LegQuoteSide }[] = [];
const NO_LEGS: readonly OrderLeg[] = [];

// Literals rather than `~/v2/path`: v3-poc may only import `~/v2/api`,
// `~/v2/providers`, `~/v2/hooks` and `~/v2/constants`.
export const OPTIONS_OPT_IN_STATUS_PATH = "/brokerage/options/opt-in/status";
export const PLANS_AND_FEATURES_PATH = "/account/plans-and-features";

/** Why the grid is empty, in the order the causes rule each other out. */
export const resolveEmptyReason = ({
  symbol,
  isCrypto,
  isUnsupportedSymbol,
  isLoading,
  expirationCount,
  selectableCount,
  isSelectionFiltered,
}: {
  symbol: string | undefined;
  isCrypto: boolean;
  isUnsupportedSymbol: boolean;
  isLoading: boolean;
  expirationCount: number;
  selectableCount: number;
  isSelectionFiltered: boolean;
}): ChainEmptyReason => {
  if (!symbol) {
    return "noSymbol";
  }
  if (isCrypto || isUnsupportedSymbol) {
    return "unsupportedSymbol";
  }
  if (expirationCount === 0 && !isLoading) {
    return "noExpirations";
  }
  // The user's own filter, not a gap in the symbol.
  if (isSelectionFiltered && selectableCount > 0) {
    return "noExpirationsInFilter";
  }
  if (isSelectionFiltered && expirationCount > 0) {
    return "noExpirations";
  }
  return "noContracts";
};

export interface UseOptionsChainWidgetControllerInput {
  groupId: GroupId | null;
  groupSymbol: string | undefined;
  widgets: WorkspaceStore["widgets"];
  tickets: WorkspaceStore["tickets"];
  store: WorkspaceStoreApi;
  setGroupSymbol: (groupId: GroupId, symbol?: string) => void;
  /**
   * Effective options level from the account. Prefer `options_trading_level`
   * (already capped); live often only has `options_approved_level`. `null`
   * means unknown — allow trading and let the ticket reject.
   */
  optionsTradingLevel: number | null;
  preferences: OptionsChainPreferences;
  setStrikeCount: (count: StrikeCount) => void;
  setSideFilter: (value: ChainSideFilter) => void;
  setExpirationFilter: (value: ExpirationFilter) => void;
  setExpirationRange: (value: ExpirationRange) => void;
  setExpirationCount: (value: ExpirationCount) => void;
  setMetricVisibility: (
    ids: readonly ChainMetricId[],
    isVisible: boolean,
  ) => void;
  selectAllMetrics: () => void;
  resetMetrics: () => void;
  reorderMetrics: (
    source: ChainMetricId,
    target: ChainMetricId,
    direction: MetricDropDirection,
  ) => void;
}

/**
 * Chain UI state, expiration pipeline, row derivation, and quote-routing
 * handlers. Market-data hooks live here because they depend on controller-owned
 * `symbol` / `expirationDates`; the connector only wires workspace, prefs, and
 * account context.
 */
export const useOptionsChainWidgetController = ({
  groupId,
  groupSymbol,
  widgets,
  tickets,
  store,
  setGroupSymbol,
  optionsTradingLevel,
  preferences,
  setStrikeCount,
  setSideFilter,
  setExpirationFilter,
  setExpirationRange,
  setExpirationCount,
  setMetricVisibility,
  selectAllMetrics,
  resetMetrics,
  reorderMetrics,
}: UseOptionsChainWidgetControllerInput) => {
  const [expiration, setExpiration] = useState<string>("");
  const [isNarrow, setIsNarrow] = useState(false);
  const [searchedSymbol, setSearchedSymbol] = useState<string | undefined>(
    undefined,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const hasGroup = groupId !== null;

  /*
    Joining a group discards the local pick so the field can show the group's
    symbol instead. Leaving starts from nothing rather than a stale local
    symbol. Adjusted during render, ahead of the queries below.
  */
  const [wasGrouped, setWasGrouped] = useState(hasGroup);
  if (hasGroup !== wasGrouped) {
    setWasGrouped(hasGroup);
    if (hasGroup) {
      setSearchedSymbol(undefined);
      setSearchQuery("");
    }
  }

  const symbol = hasGroup ? groupSymbol : searchedSymbol;

  // Same target selector the routing action uses, so what is highlighted and
  // capped always belongs to the ticket the next click would hit.
  const targetLegs = useMemo(() => {
    if (groupId === null) {
      return NO_LEGS;
    }
    const targetId = selectOptionTargetTicketId({ widgets, tickets }, groupId);
    return (targetId ? tickets[targetId]?.legs : undefined) ?? NO_LEGS;
  }, [groupId, widgets, tickets]);

  const selected = useMemo(
    () =>
      targetLegs.length === 0
        ? NO_SELECTION
        : targetLegs.map((leg) => ({
            symbol: leg.contract.symbol,
            side: leg.quoteSide,
          })),
    [targetLegs],
  );

  /**
   * The cap belongs to the underlying on the ticket. A click on a different one
   * replaces that order instead of adding a fifth leg, so those quotes stay
   * live even while the ticket is full.
   */
  const isTicketFull =
    targetLegs.length >= MAX_TICKET_LEGS &&
    !!symbol &&
    normalizeWorkspaceSymbol(targetLegs[0].contract.underlying_symbol) ===
      normalizeWorkspaceSymbol(symbol);

  // Reset the expiration when the underlying changes: an expiry is meaningless
  // on a different symbol, which is also why it is never persisted. Adjusted
  // during render, and before the queries below, so a stale expiry never reaches
  // a query key for the new symbol.
  const [lastSymbol, setLastSymbol] = useState(symbol);
  if (symbol !== lastSymbol) {
    setLastSymbol(symbol);
    setExpiration("");
  }

  const { asset } = useGetAsset({
    symbol: symbol ?? "",
    options: { enabled: !!symbol },
  });

  const { clock } = useGetClock();
  const isMarketOpen = clock?.is_open;

  // Crypto has no listed options, so the chain queries are skipped rather than
  // asked and answered empty.
  const isCrypto = asset?.class === "crypto";

  const { data: expirationsRes } = useGetOptionExpirations({
    assetSymbol: asset?.symbol,
    isCrypto,
  });

  const expirations = useMemo(
    () =>
      Array.from(
        new Set(
          (expirationsRes?.option_contracts ?? []).map(
            (entry) => entry.expiration_date,
          ),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [expirationsRes],
  );

  const { price: spot, isLoading: isSpotLoading } = useUnderlyingSpot({
    symbol: asset?.symbol,
    enabled: !!asset?.symbol && !isCrypto,
  });

  // Spot places the money, so without it there is no at-the-money marker, no
  // in-the-money shading and no centred strike window. Say so rather than
  // showing a chain that looks complete but is centred on nothing.
  const isSpotUnavailable = !isSpotLoading && spot === undefined;

  /**
   * After the close the contracts endpoint still returns that day's expired
   * contracts first, with `status: "active"` and `tradable: true`, so the
   * selectable set is derived from the calendar and the clock instead.
   */
  const selectableExpirations = useMemo(
    () => buildExpirationOptions(expirations, { isMarketOpen }),
    [expirations, isMarketOpen],
  );

  const expirationOptions = useMemo(
    () =>
      filterExpirations(
        selectableExpirations,
        preferences.expirationFilter,
        preferences.expirationRange,
      ),
    [
      selectableExpirations,
      preferences.expirationFilter,
      preferences.expirationRange,
    ],
  );

  // Default to the nearest selectable expiration, and drop a selection that
  // stops being valid (symbol change, or the same-day expiry after the close).
  useEffect(() => {
    if (expirationOptions.length === 0) {
      return;
    }
    if (!expirationOptions.some((option) => option.date === expiration)) {
      setExpiration(expirationOptions[0].date);
    }
  }, [expirationOptions, expiration]);

  /**
   * The stack: the selected expiration and the ones that follow it, capped by
   * the count. Drawn from the filtered list, so a date range narrows the stack
   * as well as the picker.
   */
  const stackedExpirations = useMemo(() => {
    const anchor = expirationOptions.findIndex(
      (option) => option.date === expiration,
    );
    if (anchor === -1) {
      return [];
    }
    return expirationOptions.slice(
      anchor,
      anchor + preferences.expirationCount,
    );
  }, [expirationOptions, expiration, preferences.expirationCount]);

  const expirationDates = useMemo(
    () => stackedExpirations.map((option) => option.date),
    [stackedExpirations],
  );

  const {
    optionContracts,
    feed,
    snapshotsReceivedAt,
    isLoading,
    hasError,
    isRefreshError,
    isUnsupportedSymbol,
    refetch,
  } = useGetOptionsData({
    asset,
    expirationDates,
    isCrypto,
  });

  const metrics = useMemo(
    () =>
      resolveVisibleMetrics({
        enabledMetricIds: preferences.metricIds,
        metricOrder: preferences.metricOrder,
        isNarrow,
      }),
    [preferences.metricIds, preferences.metricOrder, isNarrow],
  );

  /** Contracts grouped by expiration, so each stacked section builds its own ladder. */
  const contractsByExpiration = useMemo(() => {
    const grouped = new Map<string, typeof optionContracts>();
    for (const contract of optionContracts) {
      const existing = grouped.get(contract.expiration_date);
      if (existing) {
        existing.push(contract);
      } else {
        grouped.set(contract.expiration_date, [contract]);
      }
    }
    return grouped;
  }, [optionContracts]);

  /**
   * One section per stacked expiration. The strike window is applied per
   * section, so every ladder is centred on the money and the sections line up
   * strike for strike — which is the whole point of stacking them.
   */
  /*
    Rows keep their identity across a poll unless their values moved, so the
    grid can skip redrawing the strikes that did not. Held per expiration
    because a strike only means the same row within its own ladder.
  */
  const previousRows = useRef(new Map<string, readonly ChainRow[]>());

  const sections = useMemo(() => {
    const carried = new Map<string, readonly ChainRow[]>();
    const built = stackedExpirations
      .map((option) => {
        const rows = reuseUnchangedRows(
          previousRows.current.get(option.date) ?? [],
          windowStrikes(
            buildChainRows({
              contracts: contractsByExpiration.get(option.date) ?? [],
              spot,
            }),
            spot,
            preferences.strikeCount,
          ),
        );
        carried.set(option.date, rows);
        return {
          expiration: option.date,
          label: formatExpirationLabel(option),
          daysToExpiration: option.daysToExpiration,
          rows,
        };
      })
      .filter((section) => section.rows.length > 0);
    previousRows.current = carried;
    return built;
  }, [
    stackedExpirations,
    contractsByExpiration,
    spot,
    preferences.strikeCount,
  ]);

  const rows = useMemo(
    () => sections.flatMap((section) => section.rows),
    [sections],
  );

  /**
   * Freshness is per column, not per row: on the default feed the spread is
   * current while the last trade is 15 minutes behind, so one row-level label
   * would be wrong in both directions.
   */
  const freshnessByMetric = useMemo(() => {
    // Same rule as the banner: only a confirmed indicative feed marks the trade
    // columns delayed. While the feed is unknown the timestamps still speak for
    // themselves — a genuinely old trade is labelled stale on its own evidence.
    const isDelayedFeed = feed === "indicative";
    const newestQuoteAt = rows.reduce<number | undefined>((newest, row) => {
      const candidates = [row.call?.quote.quoteAt, row.put?.quote.quoteAt];
      for (const candidate of candidates) {
        if (
          candidate !== undefined &&
          (newest === undefined || candidate > newest)
        ) {
          newest = candidate;
        }
      }
      return newest;
    }, undefined);
    const newestTradeAt = rows.reduce<number | undefined>((newest, row) => {
      const candidates = [row.call?.quote.lastAt, row.put?.quote.lastAt];
      for (const candidate of candidates) {
        if (
          candidate !== undefined &&
          (newest === undefined || candidate > newest)
        ) {
          newest = candidate;
        }
      }
      return newest;
    }, undefined);

    const quoteFreshness = classifyFreshness({
      timestamp: newestQuoteAt,
      isDelayedFeed: false,
      isMarketOpen,
    });
    const tradeFreshness = classifyFreshness({
      timestamp: newestTradeAt,
      isDelayedFeed,
      isMarketOpen,
    });

    const map: Record<string, ValueFreshness> = {};
    for (const metric of [...metrics.scrollable, ...metrics.sticky]) {
      map[metric.id] =
        metric.transport === "quote"
          ? quoteFreshness
          : metric.transport === "delayed"
            ? tradeFreshness
            : metric.transport === "endOfDay"
              ? "endOfDay"
              : isMarketOpen === false
                ? "closed"
                : "live";
    }
    return map;
  }, [rows, metrics, feed, isMarketOpen]);

  /**
   * Greeks and implied volatility are present on exactly the same contracts
   * every time, and on same-day expiry they are absent for the whole chain, so
   * the widget explains the empty columns instead of looking broken.
   */
  const isGreeksUnavailable = useMemo(
    () =>
      rows.length > 0 &&
      rows.every((row) => !row.call?.quote.greeks && !row.put?.quote.greeks),
    [rows],
  );

  const isSelectionFiltered =
    expirationOptions.length === 0 ||
    !expirationOptions.some((option) => option.date === expiration);

  const emptyReason = resolveEmptyReason({
    symbol,
    isCrypto,
    isUnsupportedSymbol,
    isLoading,
    expirationCount: expirations.length,
    selectableCount: selectableExpirations.length,
    isSelectionFiltered,
  });

  const canTrade = optionsTradingLevel === null || optionsTradingLevel > 0;

  const onQuoteSelect = useCallback(
    (leg: ChainLeg, price: number | undefined, quoteSide: LegQuoteSide) => {
      if (groupId === null || !canTrade || !symbol) {
        return;
      }
      const state = store.getState();
      const newTicketId = crypto.randomUUID();
      // Pre-allocated so the store can insert a ticket inside the same
      // transaction when no eligible target exists.
      state.routeOptionContract({
        sourceGroupId: groupId,
        sourceSymbol: symbol,
        contract: leg.contract,
        price,
        quoteSide,
        newTicket: {
          id: newTicketId,
          placement: createDefaultPlacement(
            newTicketId,
            "orderTicket",
            Object.values(state.placements),
          ),
        },
      });
    },
    [groupId, symbol, canTrade, store],
  );

  const onMetricVisibilityChange = useCallback(
    (ids: readonly ChainMetricId[], isVisible: boolean) =>
      setMetricVisibility(ids, isVisible),
    [setMetricVisibility],
  );

  const onSymbolSelect = useCallback(
    (selectedSymbol: string) => {
      const next = normalizeWorkspaceSymbol(selectedSymbol);
      setSearchQuery("");
      if (groupId !== null) {
        setGroupSymbol(groupId, next);
        return;
      }
      setSearchedSymbol(next);
    },
    [groupId, setGroupSymbol],
  );

  const onSymbolClear = useCallback(() => {
    setSearchQuery("");
    if (groupId !== null) {
      setGroupSymbol(groupId, undefined);
      return;
    }
    setSearchedSymbol(undefined);
  }, [groupId, setGroupSymbol]);

  const onSearchQueryChange = useCallback(
    (next: string) => {
      if (next === "") {
        setSearchQuery("");
        if (groupId !== null) {
          setGroupSymbol(groupId, undefined);
        } else {
          setSearchedSymbol(undefined);
        }
        return;
      }

      const committed = hasGroup ? groupSymbol : searchedSymbol;
      if (
        searchQuery === "" &&
        committed &&
        next.length > committed.length &&
        next.startsWith(committed)
      ) {
        setSearchQuery(next.slice(committed.length));
        return;
      }

      setSearchQuery(next);
    },
    [
      groupId,
      groupSymbol,
      hasGroup,
      searchQuery,
      searchedSymbol,
      setGroupSymbol,
    ],
  );

  return {
    symbol,
    searchValue: searchQuery !== "" ? searchQuery : (symbol ?? ""),
    onSearchQueryChange,
    onSymbolSelect,
    onSymbolClear,
    expirations: expirationOptions,
    expiration: expiration || undefined,
    onExpirationChange: setExpiration,
    sections: isSelectionFiltered ? EMPTY_SECTIONS : sections,
    rows: isSelectionFiltered ? EMPTY_ROWS : rows,
    metrics,
    freshnessByMetric,
    strikeCount: preferences.strikeCount,
    onStrikeCountChange: setStrikeCount,
    sideFilter: preferences.sideFilter,
    onSideFilterChange: setSideFilter,
    hasExpirations: selectableExpirations.length > 0,
    expirationFilter: preferences.expirationFilter,
    onExpirationFilterChange: setExpirationFilter,
    expirationRange: preferences.expirationRange,
    onExpirationRangeChange: setExpirationRange,
    expirationCount: preferences.expirationCount,
    onExpirationCountChange: setExpirationCount,
    visibleMetricIds: preferences.metricIds,
    metricOrder: preferences.metricOrder,
    onMetricVisibilityChange,
    onReorderColumns: reorderMetrics,
    onSelectAllColumns: selectAllMetrics,
    onResetMetrics: resetMetrics,
    onNarrowChange: setIsNarrow,
    feedState: { feed, isMarketOpen },
    permissions: { optionsTradingLevel, canTrade },
    hasGroup,
    lastUpdatedAt: snapshotsReceivedAt,
    isTicketFull,
    isLoading,
    hasError,
    isRefreshError,
    onRetry: refetch,
    emptyReason,
    isGreeksUnavailable,
    isSpotUnavailable,
    spot,
    selected,
    onQuoteSelect,
    applyHref: OPTIONS_OPT_IN_STATUS_PATH,
    realTimeHref: PLANS_AND_FEATURES_PATH,
  };
};
